import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  adminPassword,
  adminUsername,
  barbersLimitedRegistrationApiUrl,
} from '../../config';
import { BarberServices } from '../../types/barber';
import { BusinessHours, Shop, ShopBarber } from '../../types/shop';
import JSONbig from 'json-bigint';

const response = [
  (data: any) => {
    if (typeof data === 'string') {
      try {
        data = JSONbig.parse(data);
      } catch (e) {
        console.error(e);
      }
    }
    return data;
  },
];

export const addShopToShopOwner = async (
  shop: Shop,
  shopOwnerId: bigint
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shopOwners/${shopOwnerId}/shops`,
    data: JSONbig.stringify(shop),
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const updateShopOwnerUserId = async (
  shopOwnerId: bigint,
  userId: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopOwners/${shopOwnerId}/userId`,
    params: { userId },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const updateShopBarberUserId = async (
  shopBarberId: bigint,
  userId: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopBarbers/${shopBarberId}/userId`,
    params: { userId },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const updateBarberUserId = async (
  barberId: bigint,
  userId: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/userId`,
    params: { userId },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const addBarberToShop = async (
  payload: ShopBarber,
  shopId: bigint
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/barber`,
    data: JSONbig.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const shopProfileImageUpload = async (
  file: File | undefined,
  shopId: bigint,
  token: string
): Promise<AxiosResponse> => {
  if (!file) {
    console.error('File is required');
    return Promise.reject('File is required');
  }
  const formData: FormData = new FormData();
  formData.append('file', file);
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/profile/images`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const barberProfileImageUpload = async (
  file: File | undefined,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  if (!file) {
    console.error('File is required');
    return Promise.reject('File is required');
  }
  const formData: FormData = new FormData();
  formData.append('file', file);
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/profile/images`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const barberImageUpload = async (
  file: File | undefined,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  if (!file) {
    console.error('File is required');
    return Promise.reject('File is required');
  }
  const formData: FormData = new FormData();
  formData.append('file', file);
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/images`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const shopImageUpload = async (
  file: File | undefined,
  shopId: bigint,
  token: string
): Promise<AxiosResponse> => {
  if (!file) {
    console.error('File is required');
    return Promise.reject('File is required');
  }
  const formData: FormData = new FormData();
  formData.append('file', file);
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/images`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const shopBarberImageUpload = async (
  file: File | undefined,
  shopId: bigint,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  if (!file) {
    console.error('File is required');
    return Promise.reject('File is required');
  }
  const formData: FormData = new FormData();
  formData.append('file', file);
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/barbers/${barberId}/images`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const addServiceToBarber = async (
  payload: BarberServices,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/service`,
    data: payload,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const addBusinessHoursToBarber = async (
  payload: BusinessHours,
  barberId: bigint
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/businessHours`,
    data: JSONbig.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const addBusinessHoursToShopBarber = async (
  payload: BusinessHours,
  barberId: bigint
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shopBarbers/${barberId}/businessHours`,
    data: JSONbig.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const updateShopName = async (
  shopName: string,
  shopId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/name`,
    params: { shopName },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const shopOwnerUpdateDetails = async (
  firstName: string,
  lastName: string,
  phoneNumber: string,
  title: string,
  shopOwnerId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopOwners/${shopOwnerId}`,
    params: { firstName, lastName, phoneNumber, title },

    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const updateShopAddress = async (
  addressLineOne: string,
  addressLineTwo: string,
  city: string,
  postcode: string,
  country: string,
  shopId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/address`,
    params: { addressLineOne, addressLineTwo, city, postcode, country },

    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};
export const barberUpdatePersonalDetails = async (
  firstName: string,
  lastName: string,
  phoneNumber: string,
  title: string,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}`,
    params: { firstName, lastName, phoneNumber, title },

    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const shopBarberUpdatePersonalDetails = async (
  firstName: string,
  lastName: string,
  phoneNumber: string,
  title: string,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopBarbers/${barberId}`,
    params: { firstName, lastName, phoneNumber, title },

    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const barberUpdateService = async (
  name: string,
  price: number,
  duration: number,
  id: bigint,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/service/${id}`,
    params: { name, price, duration },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const shopBarberUpdateService = async (
  name: string,
  price: number,
  duration: number,
  id: bigint,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopBarbers/${barberId}/services/${id}`,
    params: { name, price, duration },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const shopOwnerUpdateService = async (
  name: string,
  price: number,
  duration: number,
  id: bigint,
  shopOwnerId: bigint,
  shopId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopOwners/${shopOwnerId}/shops/${shopId}/services/${id}`,
    params: { name, price, duration },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  return axios.request(config);
};

export const addServiceToShopBarber = async (
  payload: BarberServices,
  shopId: bigint,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/barber/${barberId}/services`,
    data: payload,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const shopAddAmenities = async (
  shopId: bigint,
  selectedAmenities: { [key: string]: boolean },
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PUT',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/amenities`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    data: selectedAmenities, // Send `selectedAmenities` directly
  };

  return axios.request(config);
};

export const updateShopSubscription = async (
  subscriptionId: number,
  params: {
    name?: string;
    price?: number;
    barberSeatCount?: number;
    marketingCampaignCount?: number;
    emailNotificationCount?: number;
    smsNotificationCount?: number;
    shopId?: string;
    customerId?: string;
    expiryDate?: string;
    stripeSubscriptionId?: string;
    productId?: string;
    status?:
      | 'incomplete'
      | 'incomplete_expired'
      | 'trialing'
      | 'active'
      | 'past_due'
      | 'canceled'
      | 'unpaid'
      | 'paused';
  },
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shopSubscriptions/${subscriptionId}`,
    params,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const updateBarberSubscription = async (
  subscriptionId: number,
  params: {
    name?: string;
    price?: number;
    marketingCampaignCount?: number;
    emailNotificationCount?: number;
    smsNotificationCount?: number;
    barberId?: string;
    customerId?: string;
    expiryDate?: string;
    stripeSubscriptionId?: string;
    productId?: string;
    status?:
      | 'incomplete'
      | 'incomplete_expired'
      | 'trialing'
      | 'active'
      | 'past_due'
      | 'canceled'
      | 'unpaid'
      | 'paused';
  },
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barberSubscriptions/${subscriptionId}`,
    params,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    transformResponse: response,
  };
  return axios.request(config);
};

export const updateBarberAddress = async (
  addressLineOne: string,
  addressLineTwo: string,
  city: string,
  postcode: string,
  country: string,
  barberId: bigint,
  token: string
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/address`,
    params: { addressLineOne, addressLineTwo, city, postcode, country },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  return axios.request(config);
};

export const updateBarberLocation = async (
  barberId: bigint,
  latitude: number,
  longitude: number
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/barbers/${barberId}/location`,
    params: { latitude, longitude },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};

export const updateShopLocation = async (
  shopId: bigint,
  latitude: number,
  longitude: number
): Promise<AxiosResponse> => {
  const config: AxiosRequestConfig = {
    method: 'PATCH',
    url: `${barbersLimitedRegistrationApiUrl}/shops/${shopId}/location`,
    params: { latitude, longitude },
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      username: adminUsername,
      password: adminPassword,
    },
  };
  return axios.request(config);
};
